'use client';

import { useCallback, useRef } from 'react';
import { useAppDispatch } from '@/app/store/hooks';
import { markAppAccessed } from '@/app/store/slices/userAppAccessSlice';

const DEDUPE_WINDOW_MS = 10_000;

/**
 * Fire-and-forget tracking hook for workspace app/tool tile clicks.
 *
 * Client-side dedupe: repeats within 10s for the same sysAppId are skipped
 * so rage-taps and React StrictMode double-invokes don't amplify load.
 * Never throws; navigation must never block on tracking.
 *
 * The optional `tenantIdOverride` lets a caller with authoritative tenant
 * context (e.g. AppShell, which receives `tenant` as a prop) pass the
 * tenant directly instead of relying on the slice's scope-based selector.
 * This avoids a 404 for private apps when the userAppAccess scope is stale
 * (it's set by home views only — a user navigating /main → /workspace/X/
 * apps/y skips the home and leaves scope='main', so the BE sees tenantId
 * null and rejects the visibility check for any private app).
 */
export function useTrackAppAccess() {
  const dispatch = useAppDispatch();
  const dedupe = useRef<Map<number, number>>(new Map());

  return useCallback(
    (sysAppId: number, tenantIdOverride?: number | null) => {
      if (typeof window === 'undefined') return;
      if (!sysAppId || sysAppId <= 0) return;

      const now = Date.now();
      const last = dedupe.current.get(sysAppId) ?? 0;
      if (now - last < DEDUPE_WINDOW_MS) return;

      dedupe.current.set(sysAppId, now);
      dispatch(markAppAccessed({ sysAppId, tenantIdOverride }));
    },
    [dispatch]
  );
}
